import { useState, useEffect } from "react";
import { addNewClient } from '../utils/api.js';


const AddNewSiteModal = ({ isOpen, onClose, onCreated }) => {
  const [siteName, setSiteName] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Reset state every time the modal opens (so reopening starts fresh)
  useEffect(() => {
    if (isOpen) {
      setSiteName("");
      setSiteAddress("");
      setErrors({});
      setTouched(new Set());
      setIsSubmitting(false);
      setToast(null);
    }
  }, [isOpen]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSiteNameChange = (e) => {
    const val = e.target.value;
    setSiteName(val);
    setTouched((prev) => new Set([...prev, "siteName"]));
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, siteName: "Site Name is required" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.siteName; return n; });
    }
  };

  const handleSiteAddressChange = (e) => {
    const val = e.target.value;
    setSiteAddress(val);
    setTouched((prev) => new Set([...prev, "siteAddress"]));
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, siteAddress: "Address is required" }));
    } else {
      setErrors((prev) => { const n = { ...prev }; delete n.siteAddress; return n; });
    }
  };

  const canSubmit =
    siteName.trim() &&
    siteAddress.trim() &&
    Object.keys(errors).length === 0 &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await addNewClient(siteName.trim(), siteAddress.trim());
      // Hand the new site name back to the parent so it can be appended
      // to sitesList and auto-selected. Parent owns that state.
      onCreated(siteName.trim());
      // Parent will close us via isOpen=false after handling onCreated,
      // but call onClose too for safety in case parent forgets.
      onClose();
    } catch (err) {
      console.error("Add new site failed:", err);
      setToast({
        type: "error",
        message: err.message || "Failed to add site. Please try again.",
      });
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? "ob-input-error" : ""}`;

  return (
    <>
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, top: 'var(--fr-modal-top, 0px)', background: 'rgba(0,0,0,0.6)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}
      >
        {/* Modal panel — stop propagation so clicks inside don't close */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ width: '100%', maxWidth: '480px' }}
        >
          <div className="ob-form-card" style={{ margin: 0 }}>
            <div className="ob-header" style={{ marginBottom: '16px' }}>
              <h2 className="ob-title" style={{ fontSize: '1.4rem' }}>Add New Site</h2>
              <p className="ob-subtitle">Create a new site to add to the list</p>
            </div>

            <div className="ob-form-fields">
              {/* Site Name */}
              <div className="ob-field">
                <label className="ob-label">
                  Site Name <span className="ob-label-required">*</span>
                </label>
                <input
                  type="text"
                  value={siteName}
                  onChange={handleSiteNameChange}
                  placeholder="Site name"
                  autoFocus
                  className={inputClass("siteName")}
                />
                {touched.has("siteName") && errors.siteName && (
                  <div className="ob-error">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.siteName}
                  </div>
                )}
              </div>

              {/* Site Address */}
              <div className="ob-field">
                <label className="ob-label">
                  Address <span className="ob-label-required">*</span>
                </label>
                <input
                  type="text"
                  value={siteAddress}
                  onChange={handleSiteAddressChange}
                  placeholder="Street, suburb, state"
                  className={inputClass("siteAddress")}
                />
                {touched.has("siteAddress") && errors.siteAddress && (
                  <div className="ob-error">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.siteAddress}
                  </div>
                )}
              </div>

              {/* Buttons row */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="ob-btn"
                  style={{
                    flex: 1, background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)', color: '#cbd5e1',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="ob-btn ob-btn-submit"
                  style={{ flex: 1 }}
                >
                  {isSubmitting ? (
                    <span className="ob-spinner">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting…
                    </span>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddNewSiteModal;